import Debug from '../utils/Debug.js';

/**
 * CharacterAnimator - Animation state machine and frame timing
 *
 * Manages:
 * - Animation states (idle, walk, run, punch, etc.)
 * - Frame timing and advancement
 * - Direction-based frame selection
 * - State transitions
 * - Atlas-based frame display (no flickering)
 */
export default class CharacterAnimator {
  // Default frame duration in milliseconds
  static DEFAULT_FRAME_TIME = 100;

  // Animation state configurations (names must match metadata.json)
  // returnTo: null means return to character's configured idle animation
  static STATE_CONFIG = {
    // Idle animations
    'breathing-idle': { loop: true, frameTime: 150, priority: 0 },
    'fight-stance-idle-8-frames': { loop: true, frameTime: 150, priority: 0 },
    'idle-long': { loop: true, frameTime: 150, priority: 0 },
    'idle': { loop: true, frameTime: 150, priority: 0 },

    // Movement animations
    'walking-8-frames': { loop: true, frameTime: 100, priority: 1 },
    'running-8-frames': { loop: true, frameTime: 80, priority: 2 },
    'walk-6-frames': { loop: true, frameTime: 100, priority: 1 },
    'running-6-frames': { loop: true, frameTime: 80, priority: 2 },

    // Jump animations — no returnTo so they hold on the last frames while airborne.
    // Landing is handled by CharacterController.updateAnimationState() which forces idle/walk.
    'jumping-1': { loop: false, frameTime: 100, priority: 4, isJump: true },
    'jumping-2': { loop: false, frameTime: 100, priority: 4, isJump: true },
    'jump': { loop: false, frameTime: 100, priority: 4, isJump: true },

    // Mage attack animations
    'lead-jab': { loop: false, frameTime: 80, priority: 3, returnTo: null },
    'roundhouse-kick': { loop: false, frameTime: 80, priority: 3, returnTo: null },
    'fireball': { loop: false, frameTime: 100, priority: 3, returnTo: null },

    // Boy attack animations
    'surprise-uppercut': { loop: false, frameTime: 80, priority: 3, returnTo: null },
    'hurricane-kick': { loop: false, frameTime: 80, priority: 3, returnTo: null },

    // Rogue attack animations
    'high-kick': { loop: false, frameTime: 80, priority: 3, returnTo: null },

    // Merchant attack animations
    'leg-sweep': { loop: false, frameTime: 80, priority: 3, returnTo: null },

    // Surfer attack animations
    'cross-punch': { loop: false, frameTime: 80, priority: 3, returnTo: null },
    'flying-kick': { loop: false, frameTime: 80, priority: 3, returnTo: null },

    // Bear animations
    'going-to-sleep': { loop: false, frameTime: 100, priority: 3, returnTo: null },

    // Hellrat animations
    'eating': { loop: false, frameTime: 100, priority: 3, returnTo: null },

    // Weapon-specific attack animations (selected per equipped weapon)
    'weapon-slash':         { loop: false, frameTime: 80, priority: 3, returnTo: null },
    'weapon-slash-reverse': { loop: false, frameTime: 80, priority: 3, returnTo: null },
    'weapon-thrust':        { loop: false, frameTime: 80, priority: 3, returnTo: null },
    'weapon-backslash':     { loop: false, frameTime: 80, priority: 3, returnTo: null },
    'weapon-halfslash':     { loop: false, frameTime: 80, priority: 3, returnTo: null },
    'weapon-shoot':         { loop: false, frameTime: 80, priority: 3, returnTo: null },

    // Shared animations
    'picking-up': { loop: false, frameTime: 150, priority: 3, returnTo: null },
    'drinking': { loop: false, frameTime: 100, priority: 3, returnTo: null },
    'taking-punch': { loop: false, frameTime: 80, priority: 3, returnTo: null },
    'falling-back-death': { loop: false, frameTime: 100, priority: 5 }
  };

  /**
   * Create a new CharacterAnimator
   * @param {Character} character - The character to animate
   * @param {CharacterMetadata} metadata - Character metadata with frame info
   */
  constructor(character, metadata) {
    this.character = character;
    this.metadata = metadata;

    // Atlas reference (set via setAtlas)
    this.atlas = null;

    // Current state
    this.currentState = 'breathing-idle';
    this.previousState = null;

    // Current direction (for selecting correct sprite direction)
    this.currentDirection = 'south';

    // Frame tracking
    this.currentFrame = 0;
    this.elapsedTime = 0; // Time since last frame change (ms)

    // State configuration (can be overridden)
    this.stateConfig = { ...CharacterAnimator.STATE_CONFIG };

    // Callback for when animation completes (non-looping)
    this.onAnimationComplete = null;

    // Whether animation is paused
    this.paused = false;
  }

  /**
   * Set the sprite atlas for this animator
   * @param {SpriteAtlas} atlas - The sprite atlas
   */
  setAtlas(atlas) {
    this.atlas = atlas;
    // Reset frame timing so accumulated time from the atlas-less period
    // doesn't cause a burst of frame advances on the first update.
    this.elapsedTime = 0;
    // Clamp currentFrame to the new atlas's frame count to prevent
    // out-of-bounds lookups when switching between atlases with different
    // frame counts (e.g., legacy 12-frame idle → LPC 2-frame idle).
    if (atlas) {
      const fc = atlas.getFrameCount(this.currentState, this.currentDirection);
      if (fc > 0 && this.currentFrame >= fc) {
        this.currentFrame = 0;
      }
    }
  }

  /**
   * Set the current animation state
   * @param {string} state - State name (e.g., 'idle', 'walk', 'run', 'punch')
   * @param {boolean} force - Force state change even if same state
   */
  setState(state, force = false) {
    // Check if state exists in metadata or composited atlas
    if (!this.metadata.hasAnimation(state)) {
      // LPC weapon animations may not be in the original character metadata
      // but are present in the composited atlas — accept those too
      if (!this.atlas?.animationLookup?.has(state)) {
        Debug.warn('CharacterAnimator', `Animation state '${state}' not found in metadata. Available:`, this.metadata.animationNames);
        return;
      }
    }

    // Don't change if same state (unless forced)
    if (state === this.currentState && !force) {
      return;
    }

    // Check priority (higher priority states can interrupt lower)
    const currentConfig = this.stateConfig[this.currentState] || { priority: 0 };
    const newConfig = this.stateConfig[state] || { priority: 0 };

    // Non-looping animations can only be interrupted by higher priority,
    // EXCEPT: attacks may interrupt jumps, and _jumpHold (completed jump) is freely interruptible
    if (!currentConfig.loop && !this._jumpHold && currentConfig.priority >= newConfig.priority && !force) {
      const isAttackInterruptingJump = currentConfig.isJump && !newConfig.loop && !newConfig.isJump && newConfig.priority >= 3;
      if (!isAttackInterruptingJump) {
        Debug.log('CharacterAnimator', `Animation blocked: ${state} (priority ${newConfig.priority}) cannot interrupt ${this.currentState} (priority ${currentConfig.priority})`);
        return;
      }
      // Save jump state so we can resume after attack completes
      this._savedJumpState = {
        state: this.currentState,
        frame: this.currentFrame,
        wasHolding: this._jumpHold
      };
    }

    Debug.log('CharacterAnimator', `Animation state change: ${this.currentState} -> ${state}`);

    // Change state — clear jump hold oscillation
    this._jumpHold = false;
    this.previousState = this.currentState;
    this.currentState = state;
    this.currentFrame = 0;
    this.elapsedTime = 0;

    // Update sprite with first frame
    this.updateSpriteFrame();
  }

  /**
   * Called when the sprite direction changes
   * @param {string} direction - New direction
   */
  onDirectionChange(direction) {
    if (direction !== this.currentDirection) {
      this.currentDirection = direction;
      this.updateSpriteFrame();
    }
  }

  /**
   * Update the sprite with the current frame using atlas
   */
  updateSpriteFrame() {
    const sprite = this.character.sprite;
    if (!sprite) return;

    // Use atlas for frame lookup if available
    if (this.atlas) {
      const framePos = this.atlas.getAnimationFrame(
        this.currentState,
        this.currentDirection,
        this.currentFrame
      );

      if (framePos) {
        // Update tile size BEFORE frame position so visual dims are correct
        if (framePos.tileSize !== undefined) {
          sprite.setTileSize(framePos.tileSize);
        }
        sprite.setFramePosition(framePos.bgPosition);
      }
    }
  }

  /**
   * Update the animation (called each frame)
   * @param {number} deltaTime - Time since last update in seconds
   */
  update(deltaTime) {
    if (this.paused) return;

    // Convert to milliseconds
    const deltaMs = deltaTime * 1000;

    // Get config for current state
    const config = this.stateConfig[this.currentState] || {
      loop: true,
      frameTime: CharacterAnimator.DEFAULT_FRAME_TIME
    };

    // Jump hold oscillation: alternate between last 2 frames at a slower rate
    if (this._jumpHold) {
      this._jumpHoldTimer = (this._jumpHoldTimer || 0) + deltaMs;
      if (this._jumpHoldTimer >= 200) {
        this._jumpHoldTimer -= 200;
        const fc = this.atlas
          ? this.atlas.getFrameCount(this.currentState, this.currentDirection)
          : this.metadata.getFrameCount(this.currentState, this.currentDirection);
        if (fc >= 2) {
          // Toggle between last two frames
          this.currentFrame = this.currentFrame === fc - 1 ? fc - 2 : fc - 1;
          this.updateSpriteFrame();
        }
      }
      return; // Skip normal frame advance while holding
    }

    // Get frame count from atlas or metadata
    const frameCount = this.atlas
      ? this.atlas.getFrameCount(this.currentState, this.currentDirection)
      : this.metadata.getFrameCount(this.currentState, this.currentDirection);

    if (frameCount <= 0) {
      // Don't accumulate elapsedTime when there are no frames — prevents
      // a burst of frame advances when the atlas arrives (LPC NPCs start
      // with no atlas while async composition runs in the background).

      // Non-looping state with no frames in the atlas — force back to idle
      // to prevent permanently stuck animations (e.g., weapon-slash on a
      // legacy atlas that doesn't have it).
      const cfg = this.stateConfig[this.currentState];
      if (cfg && !cfg.loop && cfg.returnTo !== undefined) {
        const idleAnim = this.character.animations?.idle || 'breathing-idle';
        this.setState(cfg.returnTo || idleAnim, true);
      }
      return;
    }

    // Accumulate time (only when we have valid frames to advance through)
    this.elapsedTime += deltaMs;

    // Advance as many frames as elapsed time allows (keeps animation speed
    // independent of game frame rate — critical at low FPS)
    let advanced = false;
    while (this.elapsedTime >= config.frameTime) {
      this.elapsedTime -= config.frameTime;

      // Advance frame
      this.currentFrame++;

      // Check if animation complete
      if (this.currentFrame >= frameCount) {
        if (config.loop) {
          // Loop back to start
          this.currentFrame = 0;
        } else {
          // Animation complete — clamp and stop advancing
          this.currentFrame = frameCount - 1;
          this.elapsedTime = 0;

          // Trigger callback
          if (this.onAnimationComplete) {
            this.onAnimationComplete(this.currentState);
          }

          // If we attacked during a jump, restore jump animation
          if (this._savedJumpState && !config.isJump) {
            const saved = this._savedJumpState;
            this._savedJumpState = null;
            // Only restore if character is still airborne
            if (!this.character.isGrounded) {
              this.previousState = this.currentState;
              this.currentState = saved.state;
              this.currentFrame = saved.frame;
              this.elapsedTime = 0;
              this._jumpHold = saved.wasHolding;
              this._jumpHoldTimer = 0;
              this.updateSpriteFrame();
              return;
            }
          }

          // Return to idle if configured (force to bypass priority)
          if (config.returnTo !== undefined) {
            const idleAnim = this.character.animations?.idle || 'breathing-idle';
            this.setState(config.returnTo || idleAnim, true);
            return;
          }

          // Jump animations: start oscillating between last 2 frames
          if (config.isJump && frameCount >= 2) {
            this._jumpHold = true;
            this._jumpHoldTimer = 0;
          }
          break;
        }
      }
      advanced = true;
    }

    // Update sprite once after all frame advances
    if (advanced) {
      this.updateSpriteFrame();
    }
  }

  /**
   * Returns true if a non-looping attack animation is currently playing.
   * Used to gate attack input so players can't spam faster than animation speed.
   */
  isPlayingAttack() {
    const config = this.stateConfig[this.currentState];
    if (!config || config.loop) return false;
    // Priority 3 = attacks/actions, but not jumps (isJump) or death (priority 5)
    return config.priority >= 3 && config.priority < 5 && !config.isJump;
  }

  /**
   * Get the current animation state
   * @returns {string} Current state name
   */
  getState() {
    return this.currentState;
  }

  /**
   * Get the current frame index
   * @returns {number} Current frame index
   */
  getFrameIndex() {
    return this.currentFrame;
  }

  /**
   * Check if currently playing a specific state
   * @param {string} state - State to check
   * @returns {boolean} True if in that state
   */
  isInState(state) {
    return this.currentState === state;
  }

  /**
   * Whether a non-looping action animation is actively playing (attack, jump, etc.).
   * Used by PlayerSync to avoid overriding actions with walk/idle.
   * Returns false during jump-hold oscillation so remote players can transition to idle.
   */
  get isPlayingAction() {
    if (this._jumpHold) return false;
    const config = this.stateConfig[this.currentState];
    return config && !config.loop;
  }

  /**
   * Pause the animation
   */
  pause() {
    this.paused = true;
  }

  /**
   * Resume the animation
   */
  resume() {
    this.paused = false;
  }

  /**
   * Reset to initial state
   */
  reset() {
    this.currentState = 'breathing-idle';
    this.currentFrame = 0;
    this.elapsedTime = 0;
    this.paused = false;
    this.updateSpriteFrame();
  }

  /**
   * Configure a state
   * @param {string} state - State name
   * @param {Object} config - State configuration
   */
  configureState(state, config) {
    this.stateConfig[state] = {
      ...this.stateConfig[state],
      ...config
    };
  }

  /**
   * Add a new animation state
   * @param {string} state - State name
   * @param {Object} config - State configuration
   */
  addState(state, config) {
    this.stateConfig[state] = {
      loop: true,
      frameTime: CharacterAnimator.DEFAULT_FRAME_TIME,
      priority: 0,
      ...config
    };
  }
}
